import { format, isToday, isYesterday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { ChatConversation } from '@/hooks/useChat';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, Lock } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface ChatConversationListProps {
  conversations: ChatConversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  isAdmin?: boolean;
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  if (isToday(date)) return format(date, 'HH:mm', { locale: fr });
  if (isYesterday(date)) return 'Hier';
  return format(date, 'dd/MM', { locale: fr });
}

export function ChatConversationList({ 
  conversations, 
  activeId, 
  onSelect,
  isAdmin 
}: ChatConversationListProps) {
  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <MessageCircle className="h-12 w-12 mb-4 opacity-20" />
        <p className="text-sm">Aucune conversation</p>
      </div>
    );
  }

  return (
    <ScrollArea className="flex-1">
      <div className="divide-y">
        {conversations.map((conv) => (
          <button
            key={conv.id}
            onClick={() => onSelect(conv.id)}
            className={cn(
              'w-full p-4 text-left hover:bg-muted/50 transition-colors',
              activeId === conv.id && 'bg-muted'
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium truncate text-sm">
                    {conv.subject}
                  </p>
                  {conv.status === 'closed' && (
                    <Lock className="h-3 w-3 text-muted-foreground shrink-0" />
                  )}
                </div>
                {isAdmin && conv.user && (
                  <p className="text-xs text-muted-foreground truncate mt-0.5">
                    {conv.user.name}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatDate(conv.last_message_at)}
                </span>
                {(conv.unread_count || 0) > 0 && (
                  <Badge variant="default" className="h-5 min-w-5 flex items-center justify-center text-xs">
                    {conv.unread_count}
                  </Badge>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </ScrollArea>
  );
}
